import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { useCaseStudies, useDeleteCaseStudy, useUpdateCaseStudy } from '@/hooks/useCaseStudies';
import { useTranslation } from '@/features/i18n';
import type { CaseStudy } from '@/types/database';

export function CaseStudiesAdminPage() {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState<CaseStudy | null>(null);

  const { data: caseStudies, isLoading } = useCaseStudies({ includeUnpublished: true });
  const deleteCaseStudy = useDeleteCaseStudy();
  const updateCaseStudy = useUpdateCaseStudy();
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (caseToDelete) {
      await deleteCaseStudy.mutateAsync(caseToDelete.id);
      setDeleteModalOpen(false);
      setCaseToDelete(null);
    }
  };

  const handleTogglePublish = async (cs: CaseStudy) => {
    await updateCaseStudy.mutateAsync({
      id: cs.id,
      status: cs.status === 'published' ? 'draft' : 'published',
      published_at: cs.status === 'draft' ? new Date().toISOString() : cs.published_at,
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.caseStudies.title} noIndex />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.caseStudies.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.caseStudies.subtitle}
            </p>
          </div>
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            {t.admin.caseStudies.newCase}
          </Button>
        </div>

        {caseStudies && caseStudies.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {caseStudies.map((cs, index) => (
              <motion.div
                key={cs.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-3 sm:p-4 lg:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                    <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
                      {cs.cover_image_url && (
                        <img
                          src={cs.cover_image_url}
                          alt={cs.title}
                          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                          <h3 className="font-semibold text-neutral-900 dark:text-white text-sm sm:text-base truncate max-w-[150px] sm:max-w-none">
                            {cs.title}
                          </h3>
                          <div className="flex flex-wrap gap-1">
                            <Badge variant={cs.status === 'published' ? 'success' : 'warning'} className="text-xs">
                              {cs.status === 'published' ? t.admin.common.published : t.admin.common.draft}
                            </Badge>
                            {cs.is_featured && <Badge variant="primary" className="text-xs">{t.admin.common.featured}</Badge>}
                          </div>
                        </div>
                        <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 truncate">
                          {cs.client_name} &middot; {cs.industry}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-1 sm:gap-2 flex-shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 -mx-3 sm:mx-0 px-3 sm:px-0 mt-2 sm:mt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePublish(cs)}
                        title={cs.status === 'published' ? t.admin.common.unpublish : t.admin.common.publish}
                        className="p-2"
                      >
                        {cs.status === 'published' ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button variant="ghost" size="sm" className="p-2">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={() => {
                          setCaseToDelete(cs);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t.admin.caseStudies.noCases}
            description={t.admin.caseStudies.noCasesDesc}
            action={
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                {t.admin.caseStudies.createFirst}
              </Button>
            }
          />
        )}
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.caseStudies.deleteCase}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDelete.replace('{name}', caseToDelete?.title || '')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deleteCaseStudy.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
